import { useQuery } from "@tanstack/react-query";
import { createContext, useMemo, type ReactNode } from "react";

import { ApiError, apiFetch } from "@/lib/api";
import type { CurrentUser } from "@/lib/auth/types";

interface AuthContextValue {
  user: CurrentUser | null;
  isLoading: boolean;
  error: Error | null;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** Reads /api/me and exposes the current user to descendants. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiFetch<CurrentUser>("/me", { skipAuthRedirect: true }),
    staleTime: 5 * 60_000,
    retry: (n, err) => !(err instanceof ApiError && err.status === 401) && n < 1,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      isLoading,
      error: error instanceof Error ? error : null,
    }),
    [data, isLoading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
